import React from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { sum, kebabCase } from 'lodash';
import { EventSalesStats } from '../../model/event';
import { Tooltip } from '../Tooltip/Tooltip';
import { PieChart } from './../Charts/PieChart';
import { t } from '../../locales/translate';
import { formatPercentage } from '../../util/util';
import './SalesDonut.css';

interface Props extends GlobalProps {
  data: EventSalesStats;
  legendType?: 'tooltip' | 'aside';
  size: 'table' | 'medium' | 'full';
}

interface Segment {
  name: keyof EventSalesStats;
  value: number;
}

const displayedKeys: Array<keyof EventSalesStats> = [
  'available',
  'systemSold',
  'systemFree',
  'processing',
  'local',
  'cart',
  'reserved',
  'blocked',
];

export function SalesDonut({ data, legendType = 'tooltip', size }: Props): JSX.Element | null {
  const total = sum(displayedKeys.map((e) => data[e] ?? 0));
  const className = classnames('sales-donut', `sales-donut--size-${size}`, `sales-donut--${legendType}`);

  const segments: Segment[] = displayedKeys
    .map((e) => ({
      name: e,
      value: data[e] ?? 0,
    }))
    .filter((e) => e.value > 0);

  if (segments.length === 0) {
    return null;
  }

  function renderLegend(): JSX.Element {
    return (
      <table className="sales-donut__legend">
        <tbody>
          {segments.map((segment) => (
            <tr
              key={segment.name}
              className={
                segment.name === 'systemFree' || segment.name === 'processing'
                  ? 'sales-donut__legend-companion'
                  : undefined
              }
            >
              <td className="sales-donut__legend-color">
                <div className={`sales-donut__dot sales-donut__dot--${kebabCase(segment.name)}`} />
              </td>
              <td className="sales-donut__legend-name">{t.model.status[segment.name]}</td>
              <td className="sales-donut__legend-value">{segment.value}</td>
              <td className="sales-donut__legend-percentage">
                ({formatPercentage((segment.value * 1.0) / total, { maximumFractionDigits: 1 })})
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td className="sales-donut__legend-name">{t.model.contingent.capacity}</td>
            <td className="sales-donut__legend-value">{total}</td>
            <td className="sales-donut__legend-percentage">({formatPercentage(1)})</td>
          </tr>
        </tfoot>
      </table>
    );
  }

  if (legendType === 'aside') {
    return (
      <div className={className}>
        <PieChart data={segments} />
        {renderLegend()}
      </div>
    );
  } else {
    return (
      <Tooltip
        position="left"
        elevated
        gap="medium"
        className="sales-donut__tooltip"
        reference={
          <div className={className}>
            <PieChart data={segments} />
          </div>
        }
        disableRepositioning={false}
      >
        {renderLegend()}
      </Tooltip>
    );
  }
}
